/**
 * Shared Web Audio playback engine.
 *
 * iOS Safari's autoplay unlock is per-<audio>-element: resuming one element
 * inside a user gesture does not unlock a different <audio> element played
 * later (confirmed on iPad, iOS 12.3.1). A Web Audio AudioContext behaves
 * differently - once resumed inside a real user gesture it stays "running"
 * for the rest of the page's life, so every sound played through it
 * afterwards (even one started later by async code, with no gesture of its
 * own) is audible automatically. Routing all narration/intro playback
 * through one shared context sidesteps the per-element limitation entirely.
 */

let audioContext = null;
let hasAttemptedContextCreation = false;

let hasUnlockedAudioEngine = false;

const bufferCache = new Map();

const RESUME_TIMEOUT_MS = 300;

const createDomException = (message, name) => {
	try {
		return new DOMException(message, name);
	} catch (_) {
		const error = new Error(message);
		error.name = name;
		return error;
	}
};

export const getAudioContext = () => {
	if (audioContext || hasAttemptedContextCreation) {
		return audioContext;
	}
	hasAttemptedContextCreation = true;

	if (typeof window === 'undefined') {
		return null;
	}

	const AudioContextClass = window.AudioContext || window.webkitAudioContext;
	if (!AudioContextClass) {
		return null;
	}

	try {
		audioContext = new AudioContextClass();
	} catch (_) {
		audioContext = null;
	}

	return audioContext;
};

const decodeAudioDataCompat = (ctx, arrayBuffer) => {
	return new Promise((resolve, reject) => {
		let settled = false;
		const settleResolve = (buffer) => {
			if (settled) return;
			settled = true;
			resolve(buffer);
		};
		const settleReject = (error) => {
			if (settled) return;
			settled = true;
			reject(error || new Error('Failed to decode audio data'));
		};

		// Old Safari only supports the callback form (no promise returned).
		// Modern browsers support both, so the returned promise and the
		// success callback can both fire - settleResolve/settleReject make
		// that safe.
		const maybePromise = ctx.decodeAudioData(
			arrayBuffer,
			settleResolve,
			settleReject
		);

		if (maybePromise && typeof maybePromise.then === 'function') {
			maybePromise.then(settleResolve, settleReject);
		}
	});
};

export const loadBuffer = (src) => {
	if (bufferCache.has(src)) {
		return bufferCache.get(src);
	}

	const ctx = getAudioContext();
	if (!ctx) {
		return Promise.reject(new Error('Web Audio API is not available'));
	}

	const promise = fetch(src)
		.then((response) => response.arrayBuffer())
		.then((arrayBuffer) => decodeAudioDataCompat(ctx, arrayBuffer))
		.catch((error) => {
			bufferCache.delete(src);
			throw error;
		});

	bufferCache.set(src, promise);
	return promise;
};

export const unlockAudioEngine = () => {
	if (hasUnlockedAudioEngine) return;
	hasUnlockedAudioEngine = true;

	const ctx = getAudioContext();
	if (!ctx) return;

	try {
		const resumeResult = ctx.resume();
		if (resumeResult && typeof resumeResult.catch === 'function') {
			resumeResult.catch(() => {});
		}
	} catch (_) {}

	// Also play a 1-sample silent buffer through it - on some WebKit builds
	// this nudges the unlock to commit immediately rather than waiting on
	// resume() alone.
	try {
		const silentBuffer = ctx.createBuffer(1, 1, ctx.sampleRate);
		const source = ctx.createBufferSource();
		source.buffer = silentBuffer;
		source.connect(ctx.destination);
		if (typeof source.start === 'function') {
			source.start(0);
		} else if (typeof source.noteOn === 'function') {
			source.noteOn(0);
		}
	} catch (_) {}
};

const ensureContextRunning = (ctx) => {
	if (ctx.state === 'running') {
		return Promise.resolve();
	}

	return new Promise((resolve, reject) => {
		let settled = false;

		const timeoutId = setTimeout(() => {
			if (settled) return;
			settled = true;
			reject(
				createDomException(
					'Timed out waiting for audio context to resume',
					'NotAllowedError'
				)
			);
		}, RESUME_TIMEOUT_MS);

		let resumeAttempt;
		try {
			resumeAttempt = ctx.resume();
		} catch (error) {
			resumeAttempt = Promise.reject(error);
		}

		Promise.resolve(resumeAttempt).then(
			() => {
				if (settled) return;
				settled = true;
				clearTimeout(timeoutId);
				if (ctx.state === 'running') {
					resolve();
				} else {
					reject(
						createDomException(
							'Audio context did not resume',
							'NotAllowedError'
						)
					);
				}
			},
			() => {
				if (settled) return;
				settled = true;
				clearTimeout(timeoutId);
				reject(
					createDomException(
						'Audio context did not resume',
						'NotAllowedError'
					)
				);
			}
		);
	});
};

/**
 * Minimal HTMLMediaElement-shaped wrapper around a Web Audio buffer source,
 * so it can drop into existing <audio>-ref based playback code (play/pause
 * UI, shared pause-other-players pool logic, etc.) unchanged.
 */
export class WebAudioVoice {
	constructor(src) {
		this.src = src;

		this._buffer = null;
		this._duration = 0;
		this._paused = true;
		this._ended = false;
		this._offset = 0;
		this._sourceNode = null;
		this._contextStartTime = null;
		this._loadPromise = null;
		this._pendingPlay = null;
		this._playToken = 0;
		this._listeners = {};
	}

	addEventListener(type, handler) {
		if (!this._listeners[type]) {
			this._listeners[type] = [];
		}
		this._listeners[type].push(handler);
	}

	removeEventListener(type, handler) {
		const handlers = this._listeners[type];
		if (!handlers) return;
		const index = handlers.indexOf(handler);
		if (index !== -1) {
			handlers.splice(index, 1);
		}
	}

	_emit(type) {
		const handlers = this._listeners[type];
		if (!handlers || !handlers.length) return;
		handlers.slice().forEach((handler) => {
			try {
				handler({type});
			} catch (error) {
				console.log('WebAudioVoice listener error', error);
			}
		});
	}

	load() {
		if (this._loadPromise) {
			return this._loadPromise;
		}

		this._loadPromise = loadBuffer(this.src).then((buffer) => {
			this._buffer = buffer;
			this._duration = buffer.duration;
			this._emit('durationchange');
			this._emit('loadedmetadata');
			this._emit('loadeddata');
			this._emit('canplay');
			return buffer;
		});

		return this._loadPromise;
	}

	get duration() {
		return this._duration;
	}

	get paused() {
		return this._paused;
	}

	get ended() {
		return this._ended;
	}

	get currentTime() {
		if (!this._paused && this._sourceNode) {
			const ctx = getAudioContext();
			if (ctx) {
				const elapsed = ctx.currentTime - this._contextStartTime;
				return this._offset + Math.max(0, elapsed);
			}
		}
		return this._offset;
	}

	set currentTime(value) {
		const wasPlaying = !this._paused;
		this._stopSourceNode();
		this._offset = Math.max(0, value || 0);
		this._ended = false;

		if (wasPlaying) {
			this._startSourceNode();
		}
	}

	play() {
		// A real <audio> element flips .paused to false synchronously as
		// part of calling play(), before the returned promise settles - so a
		// second, overlapping play() call sees .paused already false and
		// becomes a no-op. Mirror that here (rather than only flipping it
		// once the async load/resume/start chain finishes) so back-to-back
		// callers - e.g. this app's own loadeddata+canplay-driven autoplay
		// retries firing in the same tick - can't each kick off a duplicate
		// concurrent start.
		if (this._pendingPlay) {
			return this._pendingPlay;
		}

		this._paused = false;
		const token = ++this._playToken;

		const attempt = this.load()
			.then(() => {
				const ctx = getAudioContext();
				if (!ctx) {
					throw createDomException(
						'Web Audio API unavailable',
						'NotSupportedError'
					);
				}
				return ensureContextRunning(ctx);
			})
			.then(
				() => {
					this._pendingPlay = null;
					// pause() (or a newer play()) superseded this attempt
					// while it was in flight - don't start audio nobody
					// wants anymore.
					if (token !== this._playToken) return;

					if (this._ended) {
						this._offset = 0;
						this._ended = false;
					}
					this._startSourceNode();
					this._paused = false;
					this._emit('play');
				},
				(error) => {
					this._pendingPlay = null;
					if (token === this._playToken) {
						this._paused = true;
					}
					throw error;
				}
			);

		this._pendingPlay = attempt;
		return attempt;
	}

	pause() {
		// Invalidate any in-flight play() attempt even if we're already
		// "paused" from the caller's perspective (e.g. still mid-load) - see
		// the token check in play() above.
		this._playToken++;

		if (this._paused) return;

		this._offset = this.currentTime;
		this._stopSourceNode();
		this._paused = true;
		this._emit('pause');
	}

	_startSourceNode() {
		const ctx = getAudioContext();
		if (!ctx || !this._buffer) return;

		this._stopSourceNode();

		const node = ctx.createBufferSource();
		node.buffer = this._buffer;
		node.connect(ctx.destination);

		node.onended = () => {
			// stop()/currentTime already discard the node and null its
			// onended before this can fire for a node we've replaced - this
			// guard covers builds where that ordering isn't reliable.
			if (this._sourceNode !== node) return;
			this._sourceNode = null;
			this._paused = true;
			this._ended = true;
			this._offset = this._duration;
			this._emit('ended');
			// A real <audio> element fires 'pause' as part of reaching the
			// end too - existing consumers rely on that to detect
			// completion, so mirror it here.
			this._emit('pause');
		};

		const startOffset = Math.min(
			Math.max(0, this._offset),
			this._duration || this._offset
		);

		node.start(0, startOffset);

		this._sourceNode = node;
		this._contextStartTime = ctx.currentTime;
	}

	_stopSourceNode() {
		if (!this._sourceNode) return;
		const node = this._sourceNode;
		this._sourceNode = null;
		node.onended = null;
		try {
			node.stop(0);
		} catch (_) {}
		try {
			node.disconnect();
		} catch (_) {}
	}

	destroy() {
		// Invalidate any in-flight play() attempt - otherwise it could
		// resolve after teardown and start a node nobody will ever stop.
		this._playToken++;
		this._paused = true;
		this._stopSourceNode();
		this._listeners = {};
	}
}

export default WebAudioVoice;
